"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
/********************************************* CONFIG *********************************************/
var getHashedKey = "get_11rb$";
var putHashedKey = "put_xwzc9p$";
var removeHashedKey = "remove_11rb$";
var innerArray = "%A";
/************************************** THIRD-PARTY MODULES ***************************************/
var kotlin_1 = __importDefault(require("kotlin"));
/******************************************** HELPERS *********************************************/
/**
 * Convenience for adding functions with required properties to the data object
 */
var buildImmutablePropDesc = function (value) { return ({
    enumerable: false,
    configurable: false,
    writable: false,
    value: value,
}); };
/**************************************** REUSED FUNCTIONS ****************************************/
/**
 * Returns true if the HashMap/Map is empty (contains no items)
 */
var isEmpty = function isEmpty() {
    return Object.keys(this).length < 1;
};
/**
 * Remove property from the HashMap/Map, returning the value at the given key
 *
 * Example:
 *     const myObj = {a: `eh`, b: `bee`};
 *     remove.call(myObj, `a`);           // => `eh`
 *     console.log(myObj)                 // => {b: `bee`}
 */
var remove = function remove(key) {
    var oldVal = this[key];
    var idx = [this[innerArray]].findIndex(function (item) { return item[0] === key; });
    this[innerArray].splice(idx, 1);
    Reflect.deleteProperty(this, key);
    return oldVal;
};
/**
 * Get & return property from the "HashMap"/"Map" at the given key
 */
var get = function get(key) {
    return this[key];
};
/**
 * Set property at given key with given value
 */
var put = function put(key, value) {
    this[innerArray].push([key, value]);
    return (this[key] = value);
};
/**
 * Set property at given index (aka 'order added')
 */
var atIdx = function (idx) {
    return this[innerArray][idx];
};
/**
 * Get all keys
 */
var keys = function () {
    return Object.keys(this);
};
/**
 * Constructs entries object of type expected by Kotlin MutableMap objects
 */
var kotlinMapEntries = function kotlinMapEntries() {
    var _this = this;
    var keys = [];
    var mapped = this.keys().reduce(function (acc, key) {
        acc.set(key, _this[key]);
        keys.push(key);
        return acc;
    }, new Map());
    return {
        iterator: function () {
            var curPos = 0;
            var hasNext = function () { return curPos < mapped.size; };
            var next = function () {
                if (hasNext()) {
                    var curKey = keys[curPos++];
                    var curVal = mapped.get(curKey);
                    return { key: curKey, val: curVal, value: curVal };
                }
            };
            return { next: next, hasNext: hasNext };
        },
    };
};
/********************************************* EXPORT *********************************************/
/**
 * Augments any JS objects passed in to also function like a Kotlin map
 *
 * Constructed object satisifies:
 *     Kotlin.isType(obj, Kotlin.kotlin.collections.Map) === true
 *
 * Can directly get vals in the object via both obj.get(`key`) & obj[`key`]
 *
 * "Dirty" because the 'get' method ends up being a direct sibling of the hash
 * keys (which also means it can't contain data at key "get")
 *
 * For convenience, includes a few methods expected by more complex Kotlin Map types
 *
 * NOTE: this wrapper is essential, because MobX can't observe Kotlin Maps
 * For changes in data in Kotlin "Map" objects to trigger re-renders in the web
 * UI, they must be made to also behave like regular JS objects - with top-level
 * keys for MobX to automatically wrap
 */
exports.dirtyKotlinMap = function (dataObj) {
    var kotlinDataMap = Object.create(kotlin_1.default.kotlin.collections.Map.prototype);
    // Add data object keys to top-level in new Kotlin object
    for (var _i = 0, _a = Object.keys(dataObj); _i < _a.length; _i++) {
        var key = _a[_i];
        kotlinDataMap[key] = dataObj[key];
    }
    // Add basic functions for non-basic Kotlin Map types as props
    // Map simple function names to internal Kotlin 'hashed key' for each (Kotlin
    // has existing values at these hashed keys)
    // For MobX-friendliness, ensure Kotlin Map methods grab from the object's data key props
    Reflect.defineProperty(kotlinDataMap, "get", buildImmutablePropDesc(get));
    Reflect.defineProperty(kotlinDataMap, getHashedKey, buildImmutablePropDesc(get));
    Reflect.defineProperty(kotlinDataMap, "remove", buildImmutablePropDesc(remove));
    Reflect.defineProperty(kotlinDataMap, removeHashedKey, buildImmutablePropDesc(remove));
    Reflect.defineProperty(kotlinDataMap, "put", buildImmutablePropDesc(put));
    Reflect.defineProperty(kotlinDataMap, putHashedKey, buildImmutablePropDesc(put));
    Reflect.defineProperty(kotlinDataMap, "isEmpty", buildImmutablePropDesc(isEmpty));
    Reflect.defineProperty(kotlinDataMap, "keys", buildImmutablePropDesc(keys));
    Reflect.defineProperty(kotlinDataMap, innerArray, {
        configurable: false,
        enumerable: false,
        writable: true,
        value: [],
    });
    /* Get based on order added */
    Reflect.defineProperty(kotlinDataMap, "first", buildImmutablePropDesc(function first() {
        return this[innerArray][0];
    }));
    Reflect.defineProperty(kotlinDataMap, "last", buildImmutablePropDesc(function last() {
        return this[innerArray][this[innerArray].length - 1];
    }));
    Reflect.defineProperty(kotlinDataMap, "atIdx", buildImmutablePropDesc(atIdx));
    /* Special entries object, required by certain Kotlin Map types */
    Reflect.defineProperty(kotlinDataMap, "entries", {
        configurable: false,
        enumerable: false,
        get: kotlinMapEntries,
    });
    return kotlinDataMap;
};
//# sourceMappingURL=kotlin-map-factory.js.map